import { z } from 'zod'

import { AI_CHATBOT_CONFIG } from '@/Atomic/Widgets/AiChatBot/core/engine/config'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type { SessionContext } from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

type FetchSegmentMemberCountsArgs = {
  scopeToCurrentUser?: boolean
  entityType?: DataClassName.COMPANY | DataClassName.PERSON
  limit?: number
}

export const fetchSegmentMemberCountsTool = defineTool({
  name: 'fetch_segment_member_counts',
  description:
    'Return each segment (saved list) with its true member (contact) count, computed live from the ' +
    'ContactListRelation junction. Use this for "which segments have no contacts / are empty", ' +
    '"how many contacts does each segment have", and any segment-size question. The List `size` field ' +
    'is NOT populated, so never filter on it — use this tool instead. Optionally scope to the current ' +
    "user's segments (responsible) and/or to segments that hold Company or Person records.",
  inputSchema: () =>
    z.object({
      scopeToCurrentUser: z
        .boolean()
        .optional()
        .describe(
          'When true, only include segments the current user is responsible for.',
        ),
      entityType: z
        .enum([DataClassName.COMPANY, DataClassName.PERSON])
        .optional()
        .describe(
          'Restrict to segments holding this record type: "Company" or "Person".',
        ),
      limit: z
        .number()
        .optional()
        .describe(
          `Max segments to scan (default and cap ${AI_CHATBOT_CONFIG.queryDefaults.listLimitCap}).`,
        ),
    }),
  concurrencySafe: true,

  async run(rawArgs: unknown, ctx: SessionContext) {
    // FunctionDefinition<unknown> contravariance — cast args inside run(), per project pattern.
    const args = rawArgs as FetchSegmentMemberCountsArgs

    const { data, navigation: nav } = getHostRuntime()
    if (
      !data.resolveDataClass(DataClassName.LIST) ||
      !data.resolveDataClass(DataClassName.CONTACT_LIST_RELATION)
    ) {
      throw new Error('List / ContactListRelation DataClass not found')
    }

    const cap = AI_CHATBOT_CONFIG.queryDefaults.listLimitCap
    const limit = Math.min(args.limit ?? cap, cap)

    const filters: Record<string, string> = {}
    if (args.scopeToCurrentUser) {
      if (!ctx.currentUserId) {
        return {
          error:
            'Could not determine the current user to scope segments. Ask the user to retry once signed in, or omit the current-user scope.',
        }
      }
      filters.responsible = ctx.currentUserId
    }
    if (args.entityType) {
      filters.entityType = args.entityType
    }

    const segments = (await data.query(DataClassName.LIST, {
      filters,
      order: [['updatedAt', 'desc']],
      limit,
    })) as ScrivitoAccessor[]

    // One membership count per segment — identical to ListItemsCountCell, which
    // is how the Segments table renders the "Count" column (size is unpopulated).
    const withCounts = await Promise.all(
      segments.map(async (seg) => {
        const id = seg.id()
        const contactEntity =
          seg.get('entityType') === DataClassName.COMPANY
            ? DataClassName.COMPANY
            : DataClassName.PERSON
        // Count members and resolve the segment's URL in parallel. The _url lets
        // the model render each title as a clickable link ([title](_url)) for
        // easy traversal — same convention as serializeDataItems.
        const [count, url] = await Promise.all([
          data.count(DataClassName.CONTACT_LIST_RELATION, {
            filters: { list: id, contactEntity },
          }),
          nav.urlFor(seg),
        ])
        return {
          _id: id,
          _url: url ?? undefined,
          title: String(seg.get('displayTitle') ?? seg.get('title') ?? id),
          entityType: String(seg.get('entityType') ?? ''),
          type: String(seg.get('type') ?? ''),
          // count is only null when the DataClass is unknown, guarded above — coerce
          // so the type stays number for the empty-segment comparison below.
          count: count ?? 0,
        }
      }),
    )

    const emptySegments = withCounts.filter((s) => s.count === 0)

    return {
      totalScanned: withCounts.length,
      emptyCount: emptySegments.length,
      emptySegments,
      segments: withCounts,
      capped: segments.length >= limit,
      _instruction:
        'Answer the segment question from this data. For "which segments have no contacts / are empty", ' +
        'list the emptySegments. Render each segment title as a markdown link to its _url ([title](_url)) so ' +
        'the user can open it directly. If capped is true, note that only the first N segments were scanned. ' +
        'If emptySegments is empty, state that every scanned segment has at least one contact.',
    }
  },
})
